// Ringtones and alert sounds, synthesized in the browser with Web Audio (works offline). The one exception is
// "Clean ring", a short recording under /sounds/ (kept on the device after the first load). Choices and volume
// belong to the signed-in teammate: saved on the server (all their devices) and cached on this device per
// teammate, so two people sharing a computer never change each other's ringtone.

import { applySpeaker, getDevicePrefs, isIOS, onDevicePrefsChange } from "./devices";

export type SoundCategory = "ringtone" | "text" | "mention";
export interface SoundOption {
  id: string;
  label: string;
  /** Section in the picker (ringtones only). */
  group?: string;
}

const RINGTONES: SoundOption[] = [
  // Phone: sounds like a real telephone ringing
  { id: "clean", label: "Clean ring", group: "Phone" },
  { id: "crisp", label: "Crisp", group: "Phone" },
  { id: "lobby", label: "Lobby", group: "Phone" },
  { id: "frontdesk", label: "Front desk", group: "Phone" },
  { id: "pocket", label: "Pocket", group: "Phone" },
  { id: "callback", label: "Callback", group: "Phone" },
  { id: "ping", label: "Ping", group: "Phone" },
  { id: "oldphone", label: "Old phone", group: "Phone" },
  { id: "deskphone", label: "Desk phone", group: "Phone" },
  { id: "ukring", label: "Double ring", group: "Phone" },
  // 1.11.1 Fun: upbeat and rhythmic like Island, short and bright enough to hear across a room (practical phone rings)
  { id: "island", label: "Island", group: "Fun" },
  { id: "tiki", label: "Tiki", group: "Fun" },
  { id: "reggae", label: "Beach", group: "Fun" },
  { id: "samba", label: "Samba", group: "Fun" },
  { id: "ukulele", label: "Ukulele", group: "Fun" },
  { id: "chacha", label: "Cha-cha", group: "Fun" },
  { id: "bossa", label: "Bossa", group: "Fun" },
  { id: "mambo", label: "Mambo", group: "Fun" },
  { id: "bounce", label: "Bounce", group: "Fun" },
  { id: "fiesta", label: "Fiesta", group: "Fun" },
  // Musical
  { id: "sunrise", label: "Sunrise", group: "Musical" },
  { id: "lounge", label: "Lounge", group: "Musical" },
  { id: "breeze", label: "Breeze", group: "Musical" },
  { id: "cascade", label: "Cascade", group: "Musical" },
  { id: "uplift", label: "Uplift", group: "Musical" },
  { id: "glow", label: "Glow", group: "Musical" },
  { id: "marimba", label: "Marimba", group: "Musical" },
  // Modern
  { id: "spark", label: "Spark", group: "Modern" },
  { id: "orbit", label: "Orbit", group: "Modern" },
  { id: "harbor", label: "Harbor", group: "Modern" },
  { id: "office", label: "Office", group: "Modern" },
  { id: "chime", label: "Soft chime", group: "Modern" },
  { id: "pulse", label: "Pulse", group: "Modern" },
  { id: "retro", label: "Retro", group: "Modern" },
  { id: "halo", label: "Halo", group: "Modern" },
  { id: "pixel", label: "Pixel", group: "Modern" },
  { id: "signal", label: "Signal", group: "Modern" },
  { id: "echo", label: "Echo", group: "Modern" },
  { id: "neon", label: "Neon", group: "Modern" },
  { id: "flow", label: "Flow", group: "Modern" },
  { id: "tap", label: "Tap", group: "Modern" },
  // Classic
  { id: "classic", label: "Classic", group: "Classic" },
  { id: "digital", label: "Digital", group: "Classic" },
  { id: "none", label: "None", group: "Classic" },
];
const ALERTS: SoundOption[] = [
  { id: "ding", label: "Ding" },
  { id: "pop", label: "Pop" },
  { id: "tritone", label: "Tri-tone" },
  { id: "bubble", label: "Bubble" },
  { id: "glass", label: "Glass" },
  { id: "none", label: "None" },
];

/** The choices for each setting, in display order. */
export const SOUND_OPTIONS: Record<SoundCategory, readonly SoundOption[]> = {
  ringtone: RINGTONES,
  text: ALERTS,
  mention: ALERTS,
};

export interface SoundPrefs {
  /** Incoming call ringtone id. */
  ringtone: string;
  /** Incoming text message sound id. */
  text: string;
  /** Team chat / @mention sound id. */
  mention: string;
  /** 0–100. */
  volume: number;
  /** Keypad beeps when pressing digits. */
  keypadTones: boolean;
  /** Soft beep while another call is waiting. */
  waitingBeep: boolean;
  /** Ringing sound while an outgoing call rings. */
  ringback: boolean;
  /** The "call ended" snip when the other side hangs up. */
  hangupTone: boolean;
}

/** Before 6.10.11 sounds were one setting per device, shared by everyone who signed in there. */
const OLD_DEVICE_KEY = "joy.sounds.v1";
const DEFAULTS: SoundPrefs = { ringtone: "sunrise", text: "ding", mention: "pop", volume: 70, keypadTones: true, waitingBeep: true, ringback: true, hangupTone: true };
let prefsCache: SoundPrefs | null = null;
/** The signed-in teammate (null = signed out: defaults). */
let soundUser: number | null = null;
let saveToServer: ((p: SoundPrefs) => void) | null = null;
let saveTimer: number | undefined;
const userKey = (id: number) => `joy.sounds.u${id}`;
/** Ringtones that were removed, and what plays instead for anyone who had picked one. */
const RETIRED: Record<string, string> = {
  rotary: "oldphone", trill: "frontdesk", cordless: "crisp", hotel: "deskphone",
  bright: "crisp", softring: "pocket", dualtone: "crisp", smartdesk: "frontdesk",
};

const valid = (cat: SoundCategory, id: unknown): id is string => typeof id === "string" && SOUND_OPTIONS[cat].some((o) => o.id === id);

function readLocal(key: string): Partial<SoundPrefs> | null {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? "null");
    return v && typeof v === "object" ? v : null;
  } catch {
    return null;
  }
}

function clean(saved: Partial<SoundPrefs>): SoundPrefs {
  const bool = (v: unknown, d: boolean) => (typeof v === "boolean" ? v : d);
  return {
    ringtone: valid("ringtone", saved.ringtone) ? saved.ringtone : (RETIRED[String(saved.ringtone)] ?? DEFAULTS.ringtone),
    text: valid("text", saved.text) ? saved.text : DEFAULTS.text,
    mention: valid("mention", saved.mention) ? saved.mention : DEFAULTS.mention,
    volume: typeof saved.volume === "number" && Number.isFinite(saved.volume) ? clampVol(saved.volume) : DEFAULTS.volume,
    keypadTones: bool(saved.keypadTones, DEFAULTS.keypadTones),
    waitingBeep: bool(saved.waitingBeep, DEFAULTS.waitingBeep),
    ringback: bool(saved.ringback, DEFAULTS.ringback),
    hangupTone: bool(saved.hangupTone, DEFAULTS.hangupTone),
  };
}

export function getSoundPrefs(): SoundPrefs {
  if (prefsCache) return { ...prefsCache };
  prefsCache = clean(soundUser == null ? {} : readLocal(userKey(soundUser)) ?? {});
  return { ...prefsCache };
}

/**
 * Whose sounds play on this device. Call on sign-in with the teammate's id and their saved sounds from the
 * server (null if they never chose any: they start from what this device had), and with null on sign-out.
 * `save` stores a change on the server (called at most every 0.8 s while dragging the volume).
 */
export function setSoundUser(id: number | null, fromServer: Partial<SoundPrefs> | null, save: ((p: SoundPrefs) => void) | null = null) {
  soundUser = id;
  saveToServer = save;
  prefsCache = null;
  if (id != null) {
    let p: SoundPrefs;
    if (fromServer) p = clean(fromServer);
    else {
      // Nothing on the server yet: keep this teammate's sounds from this device (or what the device used to share).
      p = clean(readLocal(userKey(id)) ?? readLocal(OLD_DEVICE_KEY) ?? {});
      if (fromServer === null && save) save(p);
    }
    prefsCache = p;
    try {
      localStorage.setItem(userKey(id), JSON.stringify(p));
    } catch {
      // this session only
    }
  }
  engine.applyVolume();
}

export function setSoundPrefs(partial: Partial<SoundPrefs>): SoundPrefs {
  const p = getSoundPrefs();
  if (valid("ringtone", partial.ringtone)) p.ringtone = partial.ringtone;
  if (valid("text", partial.text)) p.text = partial.text;
  if (valid("mention", partial.mention)) p.mention = partial.mention;
  if (typeof partial.volume === "number" && Number.isFinite(partial.volume)) p.volume = clampVol(partial.volume);
  for (const k of ["keypadTones", "waitingBeep", "ringback", "hangupTone"] as const) if (typeof partial[k] === "boolean") p[k] = partial[k];
  prefsCache = p;
  if (soundUser != null) {
    try {
      localStorage.setItem(userKey(soundUser), JSON.stringify(p));
    } catch {
      // remembered for this session only
    }
    if (saveToServer) {
      clearTimeout(saveTimer);
      const save = saveToServer;
      saveTimer = window.setTimeout(() => save({ ...p }), 800);
    }
  }
  engine.applyVolume();
  return { ...p };
}

function clampVol(v: number) {
  return Math.round(Math.min(100, Math.max(0, v)));
}

// ---------------------------------------------------------------- audio engine

/** A set of sounds that can be cut off together (the ringtone, a preview, ...). */
interface Group {
  gain: GainNode;
  send: GainNode;
  nodes: Set<AudioScheduledSourceNode>;
}
interface Out {
  ctx: BaseAudioContext;
  /** Dry output for voices. */
  dest: AudioNode;
  /** How much of this sound goes to the room reverb (set per ringtone). */
  send: GainNode;
  nodes: Set<AudioScheduledSourceNode>;
}

/** A small, warm room: decaying stereo noise, darkened, as the reverb's impulse response. */
function roomImpulse(ctx: BaseAudioContext, secs = 2.2): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * secs);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    let lp = 0;
    let seed = ch ? 0x9e3779b9 : 0x85ebca6b;
    for (let i = 0; i < len; i++) {
      seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5; // xorshift: same room every time
      const noise = ((seed >>> 0) / 0xffffffff) * 2 - 1;
      const t = i / ctx.sampleRate;
      lp += (noise - lp) * (0.35 - 0.25 * Math.min(1, t / secs)); // darker as it fades
      const early = t < 0.012 ? 0 : 1;
      data[i] = lp * early * Math.pow(1 - t / secs, 2.2) * Math.exp(-t * 1.6);
    }
  }
  return buf;
}

/** master → gentle compressor → speakers, plus a shared reverb that feeds the master. */
let guardCurve: Float32Array<ArrayBuffer> | null = null;
/** Linear up to 0.7 (-3 dBFS), then a soft knee that tops out at 1.0. Input x stands for 2x. */
function peakGuardCurve(): Float32Array<ArrayBuffer> {
  if (guardCurve) return guardCurve;
  const n = 4097;
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const u = ((i / (n - 1)) * 2 - 1) * 2;
    const a = Math.abs(u);
    c[i] = Math.sign(u) * (a < 0.7 ? a : 0.7 + 0.3 * Math.tanh((a - 0.7) / 0.3));
  }
  return (guardCurve = c);
}

function buildChain(ctx: BaseAudioContext, dest: AudioNode) {
  // Everything ends in one bus, so moving sound to another speaker is a single reconnect.
  const bus = ctx.createGain();
  bus.connect(dest);
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -12;
  comp.ratio.value = 4;
  const master = ctx.createGain();
  master.connect(comp);
  comp.connect(bus);
  // Recorded rings skip the compressor (it dulled their attack and pumped up the decay, which sounded hoarse).
  // Instead a peak guard: untouched below -3 dBFS (normal volumes), gently rounded above it so the loudest
  // setting can't clip. The shaper's input is halved so its curve covers ±2 (full scale ±1 after it).
  const direct = ctx.createGain();
  const half = ctx.createGain();
  half.gain.value = 0.5;
  const guard = ctx.createWaveShaper();
  guard.curve = peakGuardCurve();
  guard.oversample = "4x";
  direct.connect(half).connect(guard).connect(bus);
  const reverb = ctx.createConvolver();
  reverb.buffer = roomImpulse(ctx);
  const reverbIn = ctx.createGain();
  reverbIn.gain.value = 0.9;
  reverbIn.connect(reverb).connect(master);
  return { bus, master, direct, reverbIn };
}

type SinkCtx = AudioContext & { setSinkId?: (id: string) => Promise<void>; sinkId?: string };
type SinkEl = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

class Engine {
  ctx: SinkCtx | null = null;
  private master: GainNode | null = null;
  private direct: GainNode | null = null;
  private bus: GainNode | null = null;
  private reverbIn: GainNode | null = null;
  /** Fallback route to a chosen speaker for browsers with HTMLMediaElement.setSinkId but not AudioContext.setSinkId. */
  private sinkEl: SinkEl | null = null;
  private streamDest: MediaStreamAudioDestinationNode | null = null;
  private appliedSink = "";
  private groups = new Map<string, Group>();

  constructor() {
    onDevicePrefsChange(() => void this.applySink());
    // iPhone shows a "now playing" card (joytravelinc.com, with a pause button) on the lock screen for as long as a
    // page's sound engine is running, even silent. So the engine sleeps once nothing has played for a few seconds,
    // and wakes up (unlock → resume) the moment a sound plays. (The check starts with the engine, in unlock().)
  }
  private sleepTimer = 0;

  private quietSince = 0;
  private sleepIfIdle() {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== "running") return (this.quietSince = 0);
    // 3.10.26 "audio always on for Joy Phone on Mac": a chosen ringer speaker plays through a hidden player (sinkEl)
    // that streams nonstop, even silence. It counted as "busy", so the engine never slept and the Mac showed Joy
    // Phone as playing all the time. Only real sounds count now; the player pauses with the engine and plays again
    // with the next sound (unlock).
    const busy = [...this.groups.values()].some((g) => g.nodes.size > 0);
    if (busy) return (this.quietSince = 0);
    if (!this.quietSince) this.quietSince = Date.now();
    else if (Date.now() - this.quietSince > 8000) {
      this.quietSince = 0;
      try {
        this.sinkEl?.pause();
      } catch {
        // nothing playing
      }
      void ctx.suspend().catch(() => {});
      clearNowPlaying();
    }
  }

  /** Creates/resumes the audio context; call from a tap so the browser allows sound. */
  unlock() {
    // During a call the engine stays paused: a tap on the call screen mustn't start it again.
    if (callAudio) return;
    try {
      if (!this.ctx || this.ctx.state === "closed") {
        const ctx = new AudioContext({ latencyHint: "interactive" }) as SinkCtx;
        this.ctx = ctx;
        const chain = buildChain(ctx, ctx.destination);
        this.bus = chain.bus;
        this.master = chain.master;
        this.direct = chain.direct;
        this.reverbIn = chain.reverbIn;
        this.appliedSink = "";
        this.applyVolume();
        void this.applySink();
        warmSamples(ctx);
        if (!this.sleepTimer) this.sleepTimer = window.setInterval(() => this.sleepIfIdle(), 1000);
      }
      // iOS reports "interrupted" after a phone call / Siri; resume() brings it back.
      if (this.ctx.state !== "running") void this.ctx.resume().catch(() => {});
      if (this.sinkEl?.paused) void this.sinkEl.play().catch(() => {});
    } catch {
      // no Web Audio: alerts still vibrate / notify
    }
  }

  get ready() {
    return !!this.ctx && this.ctx.state !== "closed";
  }

  applyVolume() {
    if (!this.ctx || !this.master) return;
    const v = getSoundPrefs().volume / 100;
    // perceptual curve: 50% sounds about half as loud
    this.master.gain.setTargetAtTime(v * v * 1.2, this.ctx.currentTime, 0.02);
    this.direct?.gain.setTargetAtTime(v * v * 1.2, this.ctx.currentTime, 0.02);
  }

  /** Routes alert sounds to the chosen ringer speaker where the browser allows it; otherwise default output. */
  async applySink() {
    const ctx = this.ctx;
    const out = this.bus;
    if (!ctx || !out) return;
    const want = getDevicePrefs().ringerId;
    if (want === this.appliedSink) return;
    this.appliedSink = want;
    try {
      if (typeof ctx.setSinkId === "function") {
        await ctx.setSinkId(want);
        return;
      }
      const elProto = typeof HTMLMediaElement !== "undefined" ? HTMLMediaElement.prototype : null;
      if (!elProto || !("setSinkId" in elProto)) return;
      if (!want) {
        // back to the normal output
        out.disconnect();
        out.connect(ctx.destination);
        this.sinkEl?.pause();
        return;
      }
      this.streamDest ??= ctx.createMediaStreamDestination();
      if (!this.sinkEl) {
        this.sinkEl = new Audio() as SinkEl;
        this.sinkEl.srcObject = this.streamDest.stream;
      }
      await this.sinkEl.setSinkId!(want);
      await this.sinkEl.play();
      out.disconnect();
      out.connect(this.streamDest);
    } catch {
      // device gone / not allowed: play on the default output
      this.appliedSink = "";
      try {
        out.disconnect();
        out.connect(ctx.destination);
        if (typeof ctx.setSinkId === "function") await ctx.setSinkId("").catch(() => {});
      } catch {
        // nothing else to try
      }
    }
  }

  /** A fresh output for a named group; anything still playing in that group is cut off first. */
  /**
   * Quiet for the length of a call: every sound stops and the page's sound engine is paused (iPhone: page audio
   * playing next to a call's echo-cancelled mic crackles, and Web Audio running beside the call makes it stutter;
   * WebKit bugs 326286, 224874, 225880). It's paused, not closed: a closed engine can't be restarted without a tap
   * on iPhone, so the next incoming call would ring silently. Taps during the call don't wake it (see unlock).
   */
  shutdown() {
    for (const name of [...this.groups.keys()]) this.stop(name);
    try {
      this.sinkEl?.pause();
    } catch {
      // nothing playing
    }
    const ctx = this.ctx;
    if (ctx && ctx.state === "running") setTimeout(() => callAudio && void ctx.suspend().catch(() => {}), 80); // after the fade-out
    clearNowPlaying();
  }

  group(name: string, replace = true, direct = false): Out | null {
    // During a call: no page sounds at all (they crackle and can make the call stutter on iPhone), except the
    // "another call is waiting" beep.
    // Keypad beeps during a call: on computers yes (you hear the key you pressed for a phone menu); on iPhone no, a
    // page sound next to the call is what crackles there (the key still reaches the other side, 9.10.18).
    // (Android phones keep the old quiet behaviour: only computers were measured.)
    if (callAudio && name !== "waiting" && !(name === "dtmf" && !isIOS() && !/Android/i.test(navigator.userAgent))) return null;
    if (callAudio) {
      // The call-waiting beep (or a keypad beep on a computer): wake the paused engine just for it (closed again
      // right after, see quietAfterCallSound).
      if (this.ctx && this.ctx.state !== "running") void this.ctx.resume().catch(() => {});
      // A chosen speaker plays through this element: it was paused with the engine, so it has to play too (9.10.18:
      // the call-waiting beep was silent on computers with a picked speaker).
      if (this.sinkEl?.paused) void this.sinkEl.play().catch(() => {});
    } else this.unlock();
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.direct || !this.reverbIn) return null;
    let g = this.groups.get(name);
    if (g && replace) {
      this.stop(name);
      g = undefined;
    }
    if (!g) {
      const gain = ctx.createGain();
      gain.connect(direct ? this.direct : this.master);
      const send = ctx.createGain();
      send.gain.value = 0.18;
      gain.connect(send).connect(this.reverbIn);
      g = { gain, send, nodes: new Set() };
      this.groups.set(name, g);
    }
    return { ctx, dest: g.gain, send: g.send, nodes: g.nodes };
  }

  stop(name: string) {
    const g = this.groups.get(name);
    if (!g || !this.ctx) return;
    this.groups.delete(name);
    const t = this.ctx.currentTime;
    // a quick fade rather than a click
    g.gain.gain.cancelScheduledValues(t);
    g.gain.gain.setValueAtTime(g.gain.gain.value, t);
    g.gain.gain.linearRampToValueAtTime(0, t + 0.04);
    for (const n of g.nodes) {
      try {
        n.stop(t + 0.05);
      } catch {
        // already stopped
      }
    }
    // let the reverb tail ring out, then tidy up
    setTimeout(() => {
      g.gain.disconnect();
      g.send.disconnect();
    }, 200);
  }

  stopAll() {
    for (const name of [...this.groups.keys()]) this.stop(name);
  }
}

/** Tells iOS nothing is playing (so the lock-screen "now playing" card goes away). */
export function clearNowPlaying() {
  try {
    const ms = navigator.mediaSession;
    if (!ms) return;
    ms.metadata = null;
    ms.playbackState = "none";
    for (const a of ["play", "pause", "stop", "seekbackward", "seekforward", "previoustrack", "nexttrack"] as MediaSessionAction[]) {
      try {
        ms.setActionHandler(a, null);
      } catch {
        // not supported here
      }
    }
  } catch {
    // no Media Session API
  }
}

/** A call is connected (or on hold) right now: see Engine.shutdown. */
let callAudio = false;
let callAudioCloser: number | undefined;
const engine = new Engine();
/**
 * Tells the sound engine a call started or ended. While a call is on, the engine is closed and no page sounds play
 * (text and @mention sounds, keypad tones, ringback); they come back as soon as the call ends.
 */
export function setCallAudio(on: boolean) {
  if (on === callAudio) return;
  callAudio = on;
  clearTimeout(callAudioCloser);
  if (on) {
    stopRingback();
    engine.shutdown();
  }
}

// ---------------------------------------------------------------- synthesis

interface ToneOpts {
  /** frequency (Hz) */
  f: number;
  /** start time (ctx seconds) */
  t: number;
  /** duration (s) */
  d: number;
  /** peak gain */
  g?: number;
  type?: OscillatorType;
  /** attack (s) */
  a?: number;
  /** "pluck" decays from the start (bells, mallets); "hold" sustains then releases */
  shape?: "pluck" | "hold";
  /** release time for "hold" */
  r?: number;
  /** glide to this frequency over `glide` seconds */
  f2?: number;
  glide?: number;
}

function tone(o: Out, x: ToneOpts) {
  const { ctx } = o;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = x.type ?? "sine";
  const t = Math.max(x.t, ctx.currentTime);
  const peak = x.g ?? 0.2;
  const a = x.a ?? 0.006;
  osc.frequency.setValueAtTime(x.f, t);
  if (x.f2) osc.frequency.exponentialRampToValueAtTime(x.f2, t + (x.glide ?? x.d));
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(peak, t + a);
  if (x.shape === "hold") {
    const r = Math.min(x.r ?? 0.05, x.d / 2);
    env.gain.setValueAtTime(peak, t + x.d - r);
    env.gain.exponentialRampToValueAtTime(0.0001, t + x.d);
  } else {
    env.gain.exponentialRampToValueAtTime(0.0001, t + x.d);
  }
  osc.connect(env).connect(o.dest);
  osc.start(t);
  osc.stop(t + x.d + 0.02);
  o.nodes.add(osc);
  osc.onended = () => {
    o.nodes.delete(osc);
    env.disconnect();
  };
}

/** A bell / chime: a few inharmonic partials that die away at different rates. */
function bell(o: Out, f: number, t: number, d: number, g: number) {
  tone(o, { f, t, d, g, a: 0.004 });
  tone(o, { f: f * 2.0, t, d: d * 0.6, g: g * 0.28, a: 0.003 });
  tone(o, { f: f * 2.76, t, d: d * 0.35, g: g * 0.12, a: 0.002 });
  tone(o, { f: f * 5.4, t, d: d * 0.15, g: g * 0.05, a: 0.002 });
}

/** A marimba-ish mallet note: fundamental plus a short 4th harmonic "knock". */
function mallet(o: Out, f: number, t: number, g: number) {
  tone(o, { f, t, d: 0.55, g, a: 0.003 });
  tone(o, { f: f * 4, t, d: 0.08, g: g * 0.22, a: 0.002 });
  tone(o, { f: f * 10, t, d: 0.03, g: g * 0.05, a: 0.001 });
}

/** MIDI note → Hz (60 = middle C). */
const n = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

interface VoiceOpts extends ToneOpts {
  /** low-pass cutoff (Hz) at the start, sweeping to `cut2` over the note */
  cut?: number;
  cut2?: number;
  q?: number;
  /** -1 (left) … 1 (right) */
  pan?: number;
  /** cents */
  detune?: number;
  /** vibrato / tremolo */
  vib?: { rate: number; depth: number };
  trem?: { rate: number; depth: number };
}

/** An oscillator with an optional filter sweep, stereo position, vibrato and tremolo. */
function voice(o: Out, x: VoiceOpts) {
  const { ctx } = o;
  const t = Math.max(x.t, ctx.currentTime);
  const osc = ctx.createOscillator();
  osc.type = x.type ?? "sine";
  osc.frequency.setValueAtTime(x.f, t);
  if (x.f2) osc.frequency.exponentialRampToValueAtTime(x.f2, t + (x.glide ?? x.d));
  if (x.detune) osc.detune.value = x.detune;
  const env = ctx.createGain();
  const peak = x.g ?? 0.2;
  const a = x.a ?? 0.006;
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(peak, t + a);
  if (x.shape === "hold") {
    const r = Math.min(x.r ?? 0.05, x.d / 2);
    env.gain.setValueAtTime(peak, t + x.d - r);
    env.gain.exponentialRampToValueAtTime(0.0001, t + x.d);
  } else env.gain.exponentialRampToValueAtTime(0.0001, t + x.d);
  const extra: AudioNode[] = [env];
  let head: AudioNode = osc;
  if (x.cut) {
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = x.q ?? 0.7;
    lp.frequency.setValueAtTime(x.cut, t);
    if (x.cut2) lp.frequency.exponentialRampToValueAtTime(x.cut2, t + x.d);
    head.connect(lp);
    head = lp;
    extra.push(lp);
  }
  head.connect(env);
  let tail: AudioNode = env;
  const lfos: OscillatorNode[] = [];
  if (x.trem) {
    const tg = ctx.createGain();
    tg.gain.value = 1 - x.trem.depth / 2;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = x.trem.rate;
    const depth = ctx.createGain();
    depth.gain.value = x.trem.depth / 2;
    lfo.connect(depth).connect(tg.gain);
    tail.connect(tg);
    tail = tg;
    lfos.push(lfo);
    extra.push(tg, depth);
  }
  if (x.vib) {
    const lfo = ctx.createOscillator();
    lfo.frequency.value = x.vib.rate;
    const depth = ctx.createGain();
    depth.gain.value = x.vib.depth;
    lfo.connect(depth).connect(osc.detune);
    lfos.push(lfo);
    extra.push(depth);
  }
  if (x.pan && typeof ctx.createStereoPanner === "function") {
    const p = ctx.createStereoPanner();
    p.pan.value = x.pan;
    tail.connect(p);
    tail = p;
    extra.push(p);
  }
  tail.connect(o.dest);
  const end = t + x.d + 0.02;
  for (const src of [osc, ...lfos]) {
    src.start(t);
    src.stop(end);
    o.nodes.add(src);
  }
  osc.onended = () => {
    o.nodes.delete(osc);
    for (const l of lfos) o.nodes.delete(l);
    for (const e of extra) e.disconnect();
  };
}

let noiseBuf: { ctx: BaseAudioContext; buf: AudioBuffer } | null = null;
/** A short filtered noise burst: shakers, brushes, the "tick" of a pick. */
function noise(o: Out, t: number, d: number, g: number, hp = 6000, pan = 0) {
  const { ctx } = o;
  if (noiseBuf?.ctx !== ctx) {
    const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let seed = 0x2545f491;
    for (let i = 0; i < data.length; i++) {
      seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5;
      data[i] = ((seed >>> 0) / 0xffffffff) * 2 - 1;
    }
    noiseBuf = { ctx, buf };
  }
  const at = Math.max(t, ctx.currentTime);
  const src = ctx.createBufferSource();
  src.buffer = noiseBuf.buf;
  const f = ctx.createBiquadFilter();
  f.type = "highpass";
  f.frequency.value = hp;
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, at);
  env.gain.exponentialRampToValueAtTime(g, at + 0.004);
  env.gain.exponentialRampToValueAtTime(0.0001, at + d);
  let tail: AudioNode = env;
  src.connect(f).connect(env);
  if (pan && typeof ctx.createStereoPanner === "function") {
    const p = ctx.createStereoPanner();
    p.pan.value = pan;
    env.connect(p);
    tail = p;
  }
  tail.connect(o.dest);
  src.start(at);
  src.stop(at + d + 0.02);
  o.nodes.add(src);
  src.onended = () => {
    o.nodes.delete(src);
    f.disconnect();
    env.disconnect();
    if (tail !== env) tail.disconnect();
  };
}

/** Electric piano (Rhodes-ish): sine body, a bell-like tine on the attack, slow tremolo. */
function epiano(o: Out, f: number, t: number, d: number, g: number, pan = 0) {
  voice(o, { f, t, d, g, a: 0.005, trem: { rate: 4.5, depth: 0.25 }, pan });
  voice(o, { f: f * 2, t, d: d * 0.5, g: g * 0.22, a: 0.003, pan });
  voice(o, { f: f * 7.1, t, d: 0.18, g: g * 0.07, a: 0.001, pan });
}

/** Steel pan: slightly stretched partials with a bright, quick bloom. */
function steelpan(o: Out, f: number, t: number, g: number, pan = 0) {
  voice(o, { f, t, d: 0.9, g, a: 0.008, pan });
  voice(o, { f: f * 2.01, t, d: 0.55, g: g * 0.45, a: 0.006, pan });
  voice(o, { f: f * 3.02, t, d: 0.3, g: g * 0.18, a: 0.004, pan });
  voice(o, { f: f * 4.1, t, d: 0.12, g: g * 0.08, a: 0.002, pan });
}

/** Vibraphone: pure tone with motor tremolo and a soft metallic overtone. */
function vibes(o: Out, f: number, t: number, d: number, g: number, pan = 0) {
  voice(o, { f, t, d, g, a: 0.004, trem: { rate: 5.5, depth: 0.45 }, pan });
  voice(o, { f: f * 4, t, d: d * 0.25, g: g * 0.1, a: 0.002, pan });
}

/** Kalimba: a quick, woody pluck with a high "buzz" partial. */
function kalimba(o: Out, f: number, t: number, g: number, pan = 0) {
  voice(o, { f, t, d: 0.9, g, a: 0.002, pan });
  voice(o, { f: f * 5.9, t, d: 0.06, g: g * 0.2, a: 0.001, pan });
  voice(o, { f: f * 3, t, d: 0.2, g: g * 0.08, a: 0.002, pan });
}

/** Harp / guitar-like pluck: a filtered triangle whose brightness falls away. */
function pluck(o: Out, f: number, t: number, g: number, d = 1.1, pan = 0) {
  voice(o, { f, t, d, g, type: "triangle", a: 0.002, cut: Math.min(f * 9, 9000), cut2: f * 1.5, pan });
  voice(o, { f: f * 2, t, d: d * 0.4, g: g * 0.15, a: 0.002, pan });
}

/** A modern synth pluck: two detuned saws through a fast-closing filter. */
function synthPluck(o: Out, f: number, t: number, g: number, d = 0.45, pan = 0) {
  for (const det of [-7, 7]) voice(o, { f, t, d, g: g * 0.5, type: "sawtooth", a: 0.003, cut: 5200, cut2: 500, q: 2, detune: det, pan: pan + det / 40 });
}

/** A soft pad note: detuned saws, filtered, slow attack and release. */
function pad(o: Out, f: number, t: number, d: number, g: number) {
  for (const det of [-9, 0, 9]) voice(o, { f, t, d, g: g / 3, type: "sawtooth", a: d * 0.35, shape: "hold", r: d * 0.4, cut: 1400, cut2: 900, detune: det, pan: det / 18 });
}

/** The plucked, slightly hollow tone of "Clean ring": odd harmonics, a soft sub, quick decay, and a 0.25 s slap echo. */
function ringPluck(o: Out, f: number, t: number, g: number, pan = 0, echo = true, echoLevel = 0.12) {
  const strike = (at: number, gg: number, p: number) => {
    voice(o, { f, t: at, d: 0.4, g: gg, a: 0.002, pan: p });
    voice(o, { f: f * 3, t: at, d: 0.25, g: gg * 0.4, a: 0.002, pan: p });
    voice(o, { f: f * 5, t: at, d: 0.15, g: gg * 0.16, a: 0.0015, pan: p });
    voice(o, { f: f / 3, t: at, d: 0.4, g: gg * 0.2, a: 0.003, pan: p });
  };
  strike(t, g, pan);
  if (echo) strike(t + 0.25, g * echoLevel, -pan);
}

// ---------------------------------------------------------------- recorded rings

declare const __APP_VERSION__: string;
let sampleBase = "/sounds/";
/** Where recorded rings are fetched from (the app uses /sounds/). */
export function setSampleBase(base: string) {
  sampleBase = base;
}
const sampleBytes = new Map<string, Promise<ArrayBuffer | null>>();
const decoded = new WeakMap<BaseAudioContext, Map<string, AudioBuffer | Promise<AudioBuffer | null>>>();

function fetchSample(name: string): Promise<ArrayBuffer | null> {
  let p = sampleBytes.get(name);
  if (!p) {
    const v = typeof __APP_VERSION__ === "string" ? `?v=${__APP_VERSION__}` : "";
    p = fetch(`${sampleBase}${name}${v}`)
      .then((r) => (r.ok ? r.arrayBuffer() : null))
      .catch(() => null);
    sampleBytes.set(name, p);
    void p.then((b) => {
      if (!b) sampleBytes.delete(name); // try again next time
    });
  }
  return p;
}

function loadSample(ctx: BaseAudioContext, name: string): Promise<AudioBuffer | null> {
  let m = decoded.get(ctx);
  if (!m) decoded.set(ctx, (m = new Map()));
  const have = m.get(name);
  if (have) return have instanceof AudioBuffer ? Promise.resolve(have) : have;
  const cache = m;
  const p = fetchSample(name)
    .then((b) => (b ? ctx.decodeAudioData(b.slice(0)) : null))
    .then(
      (buf) => {
        if (buf) cache.set(name, buf);
        else cache.delete(name);
        return buf;
      },
      () => {
        cache.delete(name);
        return null;
      }
    );
  cache.set(name, p);
  return p;
}

/** Plays a loaded recording; false (and starts loading it) if it isn't ready yet. */
function playSample(o: Out, name: string, t: number, gain = 1): boolean {
  const buf = decoded.get(o.ctx)?.get(name);
  if (!(buf instanceof AudioBuffer)) {
    void loadSample(o.ctx, name);
    return false;
  }
  const src = o.ctx.createBufferSource();
  src.buffer = buf;
  const g = o.ctx.createGain();
  g.gain.value = gain;
  src.connect(g).connect(o.dest);
  src.start(Math.max(t, o.ctx.currentTime));
  o.nodes.add(src);
  src.onended = () => {
    o.nodes.delete(src);
    g.disconnect();
  };
  return true;
}

/** Loads the recorded rings as soon as audio is unlocked, so the first incoming call rings with them. */
function warmSamples(ctx: BaseAudioContext) {
  for (const p of Object.values(RING)) if (p.sample) void loadSample(ctx, p.sample);
}

interface Pattern {
  /** seconds between repeats when looping */
  period: number;
  play(o: Out, t0: number): void;
  /** room reverb amount (0–1, default 0.18) */
  reverb?: number;
  /** loudness trim so every ringtone rings at about the same volume (default 1) */
  level?: number;
  /** a recorded ring under /sounds/ that play() uses once it has loaded */
  sample?: string;
}

const RING: Record<string, Pattern> = {
  // ---- Phone ----
  // Modern business desk phone: a warbling electronic ring, two bursts.
  deskphone: {
    period: 3.0,
    reverb: 0.06,
    level: 2.45,
    play(o, t) {
      for (const at of [0, 0.55]) {
        voice(o, { f: 1046, t: t + at, d: 0.42, g: 0.1, type: "triangle", a: 0.005, shape: "hold", r: 0.03, trem: { rate: 16, depth: 1 } });
        voice(o, { f: 1318, t: t + at, d: 0.42, g: 0.05, type: "sine", a: 0.005, shape: "hold", r: 0.03, trem: { rate: 16, depth: 1 } });
      }
    },
  },
  // British-style "ring-ring": two short double-tone rings, then a pause.
  ukring: {
    period: 3.0,
    reverb: 0.05,
    level: 0.95,
    play(o, t) {
      for (const at of [0, 0.6]) {
        for (const f of [400, 450]) voice(o, { f, t: t + at, d: 0.4, g: 0.11, a: 0.01, shape: "hold", r: 0.03, cut: 2400 });
        // a touch of the bell buzz real handsets add
        voice(o, { f: 1200, t: t + at, d: 0.4, g: 0.02, type: "square", a: 0.01, shape: "hold", r: 0.03, cut: 3000, trem: { rate: 25, depth: 1 } });
      }
    },
  },
  // "Clean ring" (Phone Ringtone Clean by SoundReality, Pixabay Content License): the recording, one 3 s cycle,
  // with its echo and reverb wash taken out, pitched down a fourth and given more low end.
  // Until the file has loaded (first ring after opening the app offline), a close synthesized copy plays instead.
  clean: {
    period: 3.0,
    reverb: 0,
    level: 1.45,
    sample: "ring-clean.mp3",
    play(o, t) {
      if (playSample(o, "ring-clean.mp3", t)) return;
      // D–F♯–A, each note struck twice (on the beat and a dotted beat later), dry.
      for (const [at, m] of [[0, 74], [0.5, 78], [1.0, 81], [1.5, 78]] as const) {
        ringPluck(o, n(m), t + at, 0.142, 0, false);
        ringPluck(o, n(m), t + at + 0.375, 0.083, 0, false);
      }
    },
  },
  // ---- In the style of Clean ring: bright hollow plucks, tight rhythm, a short slap echo ----
  // Fast alternating two-note ring, ring-ring.
  crisp: {
    period: 3.0,
    reverb: 0.04,
    level: 2.21,
    play(o, t) {
      for (const at of [0, 0.75]) {
        for (let i = 0; i < 6; i++) ringPluck(o, n(i % 2 ? 83 : 86), t + at + i * 0.0625, i === 0 ? 0.11 : 0.085, 0, false);
      }
    },
  },
  // Same beat as Clean ring, climbing A–C♯–E–A and finishing on top.
  lobby: {
    period: 3.0,
    reverb: 0.04,
    level: 2.51,
    play(o, t) {
      for (const [at, m] of [[0, 81], [0.5, 85], [1.0, 88], [1.5, 93]] as const) {
        ringPluck(o, n(m), t + at, 0.12);
        ringPluck(o, n(m), t + at + 0.375, 0.07);
      }
    },
  },
  // Steady high–low call, four times: the desk phone at a hotel front desk.
  frontdesk: {
    period: 3.0,
    reverb: 0.04,
    level: 2.43,
    play(o, t) {
      for (let k = 0; k < 4; k++) {
        ringPluck(o, n(88), t + k * 0.5, 0.11);
        ringPluck(o, n(83), t + k * 0.5 + 0.125, 0.09);
      }
    },
  },
  // Lower and warmer for a quiet office, with the echo bouncing left and right.
  pocket: {
    period: 3.2,
    reverb: 0.06,
    level: 3.16,
    play(o, t) {
      for (const [at, m] of [[0, 76], [0.25, 80], [0.5, 83], [1.0, 76], [1.25, 80], [1.5, 83]] as const) {
        ringPluck(o, n(m), t + at, 0.12, at % 0.5 ? 0.25 : -0.25, true, 0.35);
      }
    },
  },
  // A quick rising question and a slower falling answer.
  callback: {
    period: 3.0,
    reverb: 0.04,
    level: 2.34,
    play(o, t) {
      [79, 83, 86, 91].forEach((m, i) => ringPluck(o, n(m), t + i * 0.0625, 0.1, 0, false));
      [91, 86, 83, 79].forEach((m, i) => ringPluck(o, n(m), t + 0.5 + i * 0.25, 0.11));
    },
  },
  // One high note in Clean ring's beat, echoes trading sides. The most minimal.
  ping: {
    period: 3.0,
    reverb: 0.05,
    level: 4.32,
    play(o, t) {
      for (const at of [0, 0.375, 0.5, 0.875, 1.0, 1.375]) ringPluck(o, n(91), t + at, at % 0.5 ? 0.07 : 0.11, at % 0.5 ? 0.3 : -0.3);
    },
  },
  // ---- Musical ----
  // Warm electric piano rising through Cmaj9, answered by a soft high note.
  sunrise: {
    period: 3.6,
    reverb: 0.35,
    level: 1.51,
    play(o, t) {
      const notes: [number, number][] = [[0, 60], [0.16, 64], [0.32, 67], [0.48, 71], [0.64, 74]];
      for (const [at, m] of notes) epiano(o, n(m), t + at, 1.6, 0.13, (m - 67) / 14);
      epiano(o, n(79), t + 1.15, 1.8, 0.11, 0.2);
      epiano(o, n(76), t + 1.45, 2.0, 0.1, -0.2);
    },
  },
  // Calypso steel pan with a light shaker.
  island: {
    period: 2.8,
    reverb: 0.22,
    level: 1.7,
    play(o, t) {
      const mel: [number, number][] = [[0, 79], [0.18, 76], [0.36, 79], [0.54, 81], [0.9, 79], [1.08, 76], [1.26, 74], [1.44, 76]];
      for (const [at, m] of mel) steelpan(o, n(m), t + at, 0.14, (m - 77) / 10);
      for (let i = 0; i < 8; i++) noise(o, t + i * 0.18, 0.06, i % 2 ? 0.02 : 0.035, 7000, 0.3);
    },
  },
  // ---- Fun (1.11.1): like Island, a short catchy riff with a beat, then a breath. Levels measured to match Island. ----
  // Steel pan, a different calypso riff (G major), on the offbeat, with a shaker.
  tiki: {
    period: 2.8,
    reverb: 0.2,
    level: 1.34,
    play(o, t) {
      const mel: [number, number][] = [[0, 74], [0.27, 79], [0.45, 79], [0.63, 83], [0.81, 81], [1.08, 79], [1.26, 76], [1.44, 79]];
      for (const [at, m] of mel) steelpan(o, n(m), t + at, 0.14, (m - 79) / 10);
      for (let i = 0; i < 9; i++) noise(o, t + i * 0.18, 0.05, i % 2 ? 0.03 : 0.018, 7000, -0.3);
    },
  },
  // Reggae: marimba chords on the offbeat ("skank") over a round bass, two bars.
  reggae: {
    period: 2.9,
    reverb: 0.18,
    level: 1.22,
    play(o, t) {
      const chord = (at: number, ms: number[]) => ms.forEach((m, i) => mallet(o, n(m), t + at + i * 0.012, 0.09));
      for (const at of [0.2, 0.6]) chord(at, [67, 71, 74]);
      for (const at of [1.0, 1.4]) chord(at, [69, 72, 76]);
      voice(o, { f: n(43), t, d: 0.35, g: 0.2, a: 0.01, cut: 500 });
      voice(o, { f: n(45), t: t + 0.8, d: 0.35, g: 0.2, a: 0.01, cut: 500 });
      voice(o, { f: n(47), t: t + 1.2, d: 0.25, g: 0.16, a: 0.01, cut: 500 });
    },
  },
  // Samba: quick kalimba melody, a low "surdo" thump on 2, and a running shaker.
  samba: {
    period: 2.6,
    reverb: 0.15,
    level: 1.08,
    play(o, t) {
      const mel: [number, number][] = [[0, 76], [0.15, 79], [0.3, 81], [0.45, 79], [0.6, 84], [0.825, 81], [0.975, 79], [1.2, 76], [1.35, 79]];
      for (const [at, m] of mel) kalimba(o, n(m), t + at, 0.17, (m - 79) / 8);
      for (const at of [0.3, 0.9]) voice(o, { f: n(40), t: t + at, d: 0.3, g: 0.22, a: 0.004, cut: 300 });
      for (let i = 0; i < 12; i++) noise(o, t + i * 0.125, 0.04, i % 4 === 0 ? 0.04 : 0.02, 8000, 0.25);
    },
  },
  // Ukulele: two bright strums up the chords (C, G, A minor, F), then a little "ta-da".
  ukulele: {
    period: 2.8,
    reverb: 0.18,
    level: 2.07,
    play(o, t) {
      const strum = (at: number, ms: number[], g: number) => ms.forEach((m, i) => pluck(o, n(m), t + at + i * 0.018, g, 0.7, (i - 1.5) / 4));
      strum(0, [67, 72, 76, 79], 0.1);
      strum(0.3, [67, 71, 74, 79], 0.09);
      strum(0.6, [69, 72, 76, 81], 0.09);
      strum(0.9, [65, 69, 72, 77], 0.09);
      pluck(o, n(84), t + 1.25, 0.13, 0.6, 0.2);
      pluck(o, n(88), t + 1.4, 0.13, 0.9, 0.2);
    },
  },
  // Cha-cha: "one, two, cha-cha-cha" on marimba, twice, with a cowbell-ish tick.
  chacha: {
    period: 2.7,
    reverb: 0.15,
    level: 1.08,
    play(o, t) {
      const beat = [[0, 76], [0.3, 79], [0.6, 81], [0.75, 81], [0.9, 79]] as const;
      for (const off of [0, 1.05]) for (const [at, m] of beat) mallet(o, n(m + (off ? 2 : 0)), t + off + at, 0.2);
      for (const at of [0, 0.3, 0.6, 1.05, 1.35, 1.65]) voice(o, { f: 800, t: t + at, d: 0.05, g: 0.04, a: 0.001, type: "square", cut: 2400, pan: 0.3 });
    },
  },
  // Bossa nova: syncopated electric-piano chords and a soft rim click.
  bossa: {
    period: 3.0,
    reverb: 0.25,
    level: 1.7,
    play(o, t) {
      const chord = (at: number, ms: number[]) => ms.forEach((m, i) => epiano(o, n(m), t + at + i * 0.01, 0.5, 0.075, (i - 1) / 3));
      for (const at of [0, 0.3, 0.75]) chord(at, [62, 65, 69, 72]);
      for (const at of [1.05, 1.35]) chord(at, [60, 64, 67, 71]);
      for (const at of [0, 0.45, 0.75, 1.2, 1.5]) noise(o, t + at, 0.03, 0.05, 3000, -0.2);
      voice(o, { f: n(38), t, d: 0.45, g: 0.16, a: 0.01, cut: 400 });
      voice(o, { f: n(36), t: t + 1.05, d: 0.45, g: 0.16, a: 0.01, cut: 400 });
    },
  },
  // Mambo: a punchy brassy riff (bright synth plucks) with hand claps.
  mambo: {
    period: 2.6,
    reverb: 0.15,
    level: 3.96,
    play(o, t) {
      const riff: [number, number][] = [[0, 72], [0.2, 72], [0.4, 76], [0.6, 79], [0.75, 79], [1.0, 77], [1.15, 76], [1.3, 72]];
      for (const [at, m] of riff) {
        synthPluck(o, n(m), t + at, 0.12, 0.5, -0.15);
        synthPluck(o, n(m - 12), t + at, 0.05, 0.5, 0.15);
      }
      for (const at of [0.4, 0.75, 1.15]) voice(o, { f: 1500, t: t + at, d: 0.05, g: 0.035, a: 0.001, type: "square", cut: 4000, pan: 0.2 });
    },
  },
  // Bounce: a marimba line jumping octaves, like a ball bouncing down the stairs.
  bounce: {
    period: 2.6,
    reverb: 0.15,
    level: 1.02,
    play(o, t) {
      const mel: [number, number][] = [[0, 72], [0.14, 84], [0.28, 74], [0.42, 86], [0.56, 76], [0.7, 88], [0.98, 79], [1.12, 91]];
      for (const [at, m] of mel) mallet(o, n(m), t + at, 0.19);
    },
  },
  // Fiesta: steel pan calls, kalimba answers, over a shaker.
  fiesta: {
    period: 2.8,
    reverb: 0.2,
    level: 1.58,
    play(o, t) {
      for (const [at, m] of [[0, 72], [0.18, 76], [0.36, 79]] as const) steelpan(o, n(m), t + at, 0.14, -0.25);
      for (const [at, m] of [[0.6, 84], [0.72, 83], [0.84, 79]] as const) kalimba(o, n(m), t + at, 0.16, 0.25);
      for (const [at, m] of [[1.1, 74], [1.28, 77], [1.46, 81]] as const) steelpan(o, n(m), t + at, 0.14, -0.25);
      for (let i = 0; i < 9; i++) noise(o, t + i * 0.18, 0.05, 0.022, 7000, 0.1);
    },
  },
  // Jazzy vibraphone chords: Dm9 → G13 → Cmaj9.
  lounge: {
    period: 3.8,
    reverb: 0.4,
    level: 2.34,
    play(o, t) {
      const chords: [number, number[]][] = [[0, [62, 65, 69, 72, 76]], [0.7, [55, 65, 69, 71, 76]], [1.4, [60, 64, 67, 71, 74]]];
      for (const [at, notes] of chords) notes.forEach((m, i) => vibes(o, n(m), t + at + i * 0.025, 1.4, 0.06, (i - 2) / 4));
    },
  },
  // Gentle kalimba figure in A major pentatonic.
  breeze: {
    period: 3.0,
    reverb: 0.3,
    level: 1.46,
    play(o, t) {
      const mel: [number, number][] = [[0, 69], [0.2, 73], [0.4, 76], [0.6, 78], [0.8, 76], [1.1, 81], [1.3, 78], [1.5, 76]];
      for (const [at, m] of mel) kalimba(o, n(m), t + at, 0.16, (m - 75) / 9);
    },
  },
  // A harp glissando falling down the pentatonic, then two low notes.
  cascade: {
    period: 3.4,
    reverb: 0.45,
    level: 1.86,
    play(o, t) {
      const run = [86, 84, 81, 79, 76, 74, 72, 69, 67];
      run.forEach((m, i) => pluck(o, n(m), t + i * 0.09, 0.1, 1.2, (i - 4) / 6));
      pluck(o, n(60), t + 0.95, 0.13, 1.6, -0.1);
      pluck(o, n(67), t + 1.15, 0.12, 1.6, 0.1);
    },
  },
  // Bright, hopeful synth motif over a round bass note.
  uplift: {
    period: 2.8,
    reverb: 0.25,
    level: 2.48,
    play(o, t) {
      const mel: [number, number][] = [[0, 72], [0.15, 76], [0.3, 79], [0.45, 84], [0.75, 83], [0.9, 79], [1.05, 84]];
      for (const [at, m] of mel) synthPluck(o, n(m), t + at, 0.12, 0.4);
      voice(o, { f: n(48), t, d: 0.9, g: 0.16, a: 0.01, cut: 600 });
      voice(o, { f: n(55), t: t + 0.75, d: 0.6, g: 0.13, a: 0.01, cut: 600 });
    },
  },
  // A soft pad swells in under a glassy bell.
  glow: {
    period: 4.0,
    reverb: 0.5,
    level: 1.74,
    play(o, t) {
      for (const m of [57, 64, 69, 72]) pad(o, n(m), t, 2.6, 0.07);
      bell(o, n(88), t + 0.25, 2.2, 0.08);
      bell(o, n(84), t + 0.85, 2.2, 0.07);
      bell(o, n(81), t + 1.45, 2.4, 0.07);
    },
  },
  // ---- Modern ----
  // Crisp three-note "smartphone" motif, played twice.
  spark: {
    period: 2.4,
    reverb: 0.2,
    level: 2.75,
    play(o, t) {
      for (const at of [0, 0.62]) {
        [84, 88, 91].forEach((m, i) => {
          voice(o, { f: n(m), t: t + at + i * 0.09, d: 0.32, g: 0.12, type: "triangle", a: 0.002, cut: 7000, cut2: 1800 });
          voice(o, { f: n(m + 12), t: t + at + i * 0.09, d: 0.12, g: 0.03, a: 0.001 });
        });
      }
    },
  },
  // FM-style bells bouncing left and right.
  orbit: {
    period: 3.0,
    reverb: 0.35,
    level: 2.75,
    play(o, t) {
      const seq = [76, 83, 79, 86, 81, 88];
      seq.forEach((m, i) => {
        const pan = i % 2 ? 0.7 : -0.7;
        voice(o, { f: n(m), t: t + i * 0.16, d: 0.9, g: 0.1, a: 0.002, pan });
        voice(o, { f: n(m) * 3.5, t: t + i * 0.16, d: 0.25, g: 0.035, a: 0.001, pan });
      });
    },
  },
  // Calm low bell with a high answer, like a harbor buoy.
  harbor: {
    period: 3.6,
    reverb: 0.45,
    level: 1.07,
    play(o, t) {
      bell(o, n(64), t, 2.6, 0.17);
      bell(o, n(71), t + 0.55, 2.4, 0.12);
      bell(o, n(76), t + 1.1, 2.4, 0.1);
    },
  },
  // A polite desk-phone warble: quick alternating tones, softly filtered.
  office: {
    period: 3.0,
    reverb: 0.12,
    level: 1.48,
    play(o, t) {
      for (const burst of [0, 0.75]) {
        for (let i = 0; i < 8; i++) {
          voice(o, { f: i % 2 ? n(81) : n(76), t: t + burst + i * 0.06, d: 0.07, g: 0.08, type: "square", a: 0.003, shape: "hold", r: 0.02, cut: 2600 });
        }
      }
    },
  },
  // 8-bit chiptune jingle.
  // Airy rising fifths with a shimmer on top.
  halo: {
    period: 3.2,
    reverb: 0.45,
    level: 2.39,
    play(o, t) {
      const pairs: [number, number, number][] = [[0, 69, 76], [0.32, 71, 78], [0.64, 73, 80], [0.96, 76, 83]];
      for (const [at, a, b] of pairs) {
        voice(o, { f: n(a), t: t + at, d: 1.1, g: 0.09, a: 0.01, pan: -0.3 });
        voice(o, { f: n(b), t: t + at + 0.02, d: 1.0, g: 0.07, a: 0.01, pan: 0.3 });
        voice(o, { f: n(b + 12), t: t + at + 0.02, d: 0.35, g: 0.018, a: 0.004, vib: { rate: 6, depth: 12 } });
      }
    },
  },
  // Staccato syncopated mallet-synth figure.
  pixel: {
    period: 2.6,
    reverb: 0.2,
    level: 2.79,
    play(o, t) {
      const seq: [number, number][] = [[0, 79], [0.14, 86], [0.28, 83], [0.5, 79], [0.64, 86], [0.78, 88], [1.0, 83], [1.14, 86]];
      for (const [at, m] of seq) {
        voice(o, { f: n(m), t: t + at, d: 0.22, g: 0.11, a: 0.002 });
        voice(o, { f: n(m) * 3.0, t: t + at, d: 0.06, g: 0.04, a: 0.001 });
        voice(o, { f: n(m) * 5.2, t: t + at, d: 0.025, g: 0.015, a: 0.001 });
      }
    },
  },
  // Clean two-tone interval motif, sleek and business-like.
  signal: {
    period: 2.8,
    reverb: 0.25,
    level: 1.18,
    play(o, t) {
      for (const at of [0, 0.9]) {
        voice(o, { f: n(76), t: t + at, d: 0.26, g: 0.12, a: 0.008, shape: "hold", r: 0.12 });
        voice(o, { f: n(83), t: t + at + 0.18, d: 0.42, g: 0.12, a: 0.008, shape: "hold", r: 0.25 });
        voice(o, { f: n(88), t: t + at + 0.18, d: 0.42, g: 0.03, a: 0.008, shape: "hold", r: 0.25 });
      }
    },
  },
  // One pluck motif with ping-pong echoes fading away.
  echo: {
    period: 3.2,
    reverb: 0.3,
    level: 6,
    play(o, t) {
      const motif: [number, number][] = [[0, 81], [0.12, 76], [0.24, 84]];
      for (let rep = 0; rep < 4; rep++) {
        const g = 0.13 * Math.pow(0.5, rep);
        const pan = rep === 0 ? 0 : rep % 2 ? -0.75 : 0.75;
        for (const [at, m] of motif) synthPluck(o, n(m), t + at + rep * 0.42, g, 0.35, pan);
      }
    },
  },
  // Dark synth arpeggio (minor 9) over a pulsing pad.
  neon: {
    period: 3.0,
    reverb: 0.3,
    level: 3.6,
    play(o, t) {
      const arp = [57, 64, 67, 71, 72, 71, 67, 64];
      arp.forEach((m, i) => synthPluck(o, n(m + 12), t + i * 0.125, 0.11, 0.3, (i % 2 ? 0.25 : -0.25)));
      for (let i = 0; i < 4; i++) for (const m of [45, 52]) voice(o, { f: n(m), t: t + i * 0.25, d: 0.24, g: 0.05, type: "sawtooth", a: 0.06, shape: "hold", r: 0.12, cut: 700 });
    },
  },
  // Smooth bell arpeggio rising and falling.
  flow: {
    period: 3.2,
    reverb: 0.4,
    level: 3.06,
    play(o, t) {
      const up = [74, 78, 81, 85, 88, 85, 81, 78];
      up.forEach((m, i) => {
        voice(o, { f: n(m), t: t + i * 0.11, d: 0.8, g: 0.08, a: 0.003, pan: (i - 3.5) / 5 });
        voice(o, { f: n(m) * 3.5, t: t + i * 0.11, d: 0.2, g: 0.025, a: 0.001, pan: (i - 3.5) / 5 });
      });
    },
  },
  // Minimal: wood-block clicks with tonal blips.
  tap: {
    period: 2.4,
    reverb: 0.15,
    level: 3.9,
    play(o, t) {
      const hits: [number, number][] = [[0, 84], [0.15, 84], [0.45, 91], [0.75, 84], [0.9, 88]];
      for (const [at, m] of hits) {
        voice(o, { f: n(m), t: t + at, d: 0.09, g: 0.14, a: 0.001, cut: 4000 });
        voice(o, { f: n(m) * 2.3, t: t + at, d: 0.03, g: 0.05, a: 0.001 });
        voice(o, { f: 5200, t: t + at, d: 0.015, g: 0.02, a: 0.0008 });
      }
    },
  },
  retro: {
    period: 2.6,
    reverb: 0.1,
    level: 1.43,
    play(o, t) {
      const mel: [number, number, number][] = [[0, 72, 0.1], [0.12, 76, 0.1], [0.24, 79, 0.1], [0.36, 84, 0.22], [0.64, 79, 0.1], [0.76, 84, 0.3]];
      for (const [at, m, d] of mel) voice(o, { f: n(m), t: t + at, d, g: 0.055, type: "square", a: 0.002, shape: "hold", r: 0.02 });
      voice(o, { f: n(48), t, d: 0.3, g: 0.07, type: "triangle", a: 0.002, shape: "hold", r: 0.03 });
      voice(o, { f: n(55), t: t + 0.36, d: 0.3, g: 0.07, type: "triangle", a: 0.002, shape: "hold", r: 0.03 });
      voice(o, { f: n(60), t: t + 0.76, d: 0.3, g: 0.07, type: "triangle", a: 0.002, shape: "hold", r: 0.03 });
    },
  },
  // ---- Classic ----
  // North-American style two-tone ring (the original sound), with soft edges.
  classic: {
    period: 2.5,
    play(o, t) {
      for (const at of [0, 0.45]) {
        for (const f of [440, 480]) tone(o, { f, t: t + at, d: 0.4, g: 0.11, a: 0.02, shape: "hold", r: 0.04 });
      }
    },
  },
  marimba: {
    period: 2.6,
    play(o, t) {
      const C6 = 1046.5, G5 = 784, E5 = 659.3, A5 = 880;
      const notes: [number, number][] = [[0, C6], [0.15, G5], [0.3, E5], [0.45, G5], [0.6, C6], [0.9, A5], [1.05, G5], [1.2, E5]];
      for (const [at, f] of notes) mallet(o, f, t + at, 0.22);
    },
  },
  digital: {
    period: 2.2,
    level: 1.84,
    play(o, t) {
      for (const burst of [0, 0.85]) {
        for (let i = 0; i < 6; i++) {
          tone(o, { f: i % 2 ? 1318.5 : 1046.5, t: t + burst + i * 0.085, d: 0.065, g: 0.09, type: "triangle", a: 0.004, shape: "hold", r: 0.015 });
        }
      }
    },
  },
  chime: {
    period: 3.4,
    level: 1.48,
    play(o, t) {
      bell(o, 784, t, 1.6, 0.16); // G5
      bell(o, 1174.7, t + 0.38, 1.6, 0.13); // D6
      bell(o, 987.8, t + 0.76, 2.0, 0.14); // B5
    },
  },
  // A mechanical bell: a clapper striking two bells ~28 times a second, two bursts per ring.
  oldphone: {
    period: 3.0,
    level: 3.8,
    play(o, t) {
      for (const burst of [0, 0.6]) {
        for (let i = 0; i < 12; i++) {
          const f = i % 2 ? 1290 : 1075;
          const at = t + burst + i * 0.035;
          tone(o, { f, t: at, d: 0.12, g: 0.07, a: 0.002 });
          tone(o, { f: f * 2.72, t: at, d: 0.05, g: 0.018, a: 0.001 });
        }
      }
    },
  },
  pulse: {
    period: 2.4,
    level: 1.53,
    play(o, t) {
      for (const at of [0, 0.42, 0.84]) {
        tone(o, { f: 523.3, t: t + at, d: 0.34, g: 0.12, a: 0.1, shape: "hold", r: 0.18 });
        tone(o, { f: 784, t: t + at, d: 0.34, g: 0.05, a: 0.1, shape: "hold", r: 0.18 });
      }
    },
  },
};

const ALERT: Record<string, (o: Out, t: number) => void> = {
  ding: (o, t) => bell(o, 1568, t, 1.3, 0.2),
  pop: (o, t) => {
    tone(o, { f: 320, f2: 1050, glide: 0.05, t, d: 0.09, g: 0.32, a: 0.002 });
    tone(o, { f: 180, t, d: 0.05, g: 0.12, a: 0.002 });
  },
  tritone: (o, t) => {
    mallet(o, 1046.5, t, 0.18);
    mallet(o, 1318.5, t + 0.12, 0.18);
    mallet(o, 1568, t + 0.24, 0.2);
  },
  bubble: (o, t) => {
    tone(o, { f: 380, f2: 1100, glide: 0.1, t, d: 0.13, g: 0.22, a: 0.004 });
    tone(o, { f: 520, f2: 1500, glide: 0.09, t: t + 0.12, d: 0.12, g: 0.18, a: 0.004 });
  },
  glass: (o, t) => {
    const ping = (f: number, at: number, g: number) => {
      tone(o, { f, t: at, d: 1.4, g, a: 0.002 });
      tone(o, { f: f * 2.41, t: at, d: 0.7, g: g * 0.25, a: 0.002 });
      tone(o, { f: f * 3.93, t: at, d: 0.35, g: g * 0.1, a: 0.001 });
    };
    ping(2093, t, 0.12);
    ping(2637, t + 0.1, 0.09);
  },
};

function playAlert(group: string, id: string) {
  const fn = ALERT[id];
  if (!fn) return;
  const o = engine.group(group);
  if (o) fn(o, o.ctx.currentTime + 0.02);
}

// ---------------------------------------------------------------- public API

/** Plays one sample of a sound (from a settings screen; that tap also unlocks audio). */
export function preview(category: SoundCategory, id: string) {
  engine.unlock();
  engine.stop("preview");
  if (category === "ringtone") {
    const p = RING[id];
    const o = p && engine.group("preview", true, !!p.sample);
    if (o) {
      o.send.gain.value = p.reverb ?? 0.18;
      (o.dest as GainNode).gain.value = p.level ?? 1;
      const start = () => p.play(o, o.ctx.currentTime + 0.03);
      // A recorded ring: wait the moment it takes to load the first time, so the preview is the real thing.
      if (p.sample) void loadSample(o.ctx, p.sample).then(start);
      else start();
    }
    if (id !== "none") navigator.vibrate?.([300, 150, 300]);
  } else {
    playAlert("preview", id);
  }
}

/** Standard telephone keypad tones (low + high frequency, Hz). */
const DTMF: Record<string, [number, number]> = {
  "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
  "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
  "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
  "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
};
/**
 * The keypad "beep", played right here the instant a key is pressed. The tone sent down the phone line only
 * comes back through the network a moment later, which felt like a one-second lag.
 */
export function playDtmf(key: string, quiet = false) {
  const pair = DTMF[key];
  if (!pair || !getSoundPrefs().keypadTones) return;
  const o = engine.group("dtmf");
  if (!o) return;
  o.send.gain.value = 0; // dry, like a real phone
  const t = o.ctx.currentTime + 0.005;
  const g = quiet ? 0.05 : 0.09;
  for (const f of pair) tone(o, { f, t, d: 0.13, g, a: 0.004, shape: "hold", r: 0.02 });
  quietAfterCallSound();
}

/** The incoming-text sound (per the user's choice). */
export function playTextSound() {
  playAlert("text", getSoundPrefs().text);
}

/** The team chat / @mention sound (per the user's choice). */
export function playMentionSound() {
  playAlert("mention", getSoundPrefs().mention);
}

/** A soft double beep: "another call is waiting" (much quieter than a ringtone). */
export function playCallWaitingBeep() {
  if (!getSoundPrefs().waitingBeep) return;
  const o = engine.group("waiting");
  if (!o) return;
  const t = o.ctx.currentTime + 0.02;
  tone(o, { f: 660, t, d: 0.16, g: 0.07, a: 0.015, shape: "hold", r: 0.05 });
  tone(o, { f: 660, t: t + 0.32, d: 0.16, g: 0.07, a: 0.015, shape: "hold", r: 0.05 });
  navigator.vibrate?.([120, 80, 120]);
  quietAfterCallSound();
}
/** In a call: close the engine again right after a beep (nothing of ours keeps running next to the call). */
function quietAfterCallSound() {
  if (!callAudio) return;
  clearTimeout(callAudioCloser);
  callAudioCloser = window.setTimeout(() => callAudio && engine.shutdown(), 900);
}

/**
 * The standard US ring tone (440 + 480 Hz, 2 s on, 4 s off), played here while an outgoing call rings.
 * Outgoing calls don't carry the carrier's own ringing any more: with it, Telnyx went silent for ~4 s at the
 * moment of answer, and the first words ("Welcome to Delta…") were lost.
 */
// 1.10.28 "crackles only while I hear it ring, not once they pick up": the ring was played by the page's sound
// engine (Web Audio, low-latency buffers) while the call's mic was already open: the very pairing that crackles
// (see Engine.shutdown: the engine is paused during calls for that reason, but only once the call connects). The
// ring is now a recorded loop in its own small player, the same kind of player the call's audio uses (bigger
// buffers, nothing of the engine running beside the mic), on the call's speaker. (The app's leg to Telnyx is
// already connected while the other side rings, so the call's own player is busy with the call by then.)
let ringbackUrl: string | null = null;
let ringbackEl: HTMLAudioElement | null = null;
/** 6 s of the US ring (440 + 480 Hz, 2 s on, 4 s off) as a WAV file, made once. Peak 0.054: the old ring at the default volume (0.045 × 2 tones × 70%² × 1.2). */
function ringbackWav(): string {
  if (ringbackUrl) return ringbackUrl;
  const rate = 16000;
  const n = rate * 6;
  const data = new Int16Array(n);
  for (let i = 0; i < 2 * rate; i++) {
    const t = i / rate;
    const env = Math.min(1, t / 0.02, (2 - t) / 0.03); // soft edges: no click at the start or end of each ring
    data[i] = Math.round(0.027 * env * (Math.sin(2 * Math.PI * 440 * t) + Math.sin(2 * Math.PI * 480 * t)) * 32767);
  }
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const str = (o: number, x: string) => [...x].forEach((c, k) => v.setUint8(o + k, c.charCodeAt(0)));
  str(0, "RIFF"); v.setUint32(4, 36 + n * 2, true); str(8, "WAVE"); str(12, "fmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, "data"); v.setUint32(40, n * 2, true);
  new Int16Array(buf, 44).set(data);
  ringbackUrl = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
  return ringbackUrl;
}
export function startRingback() {
  if (!getSoundPrefs().ringback || typeof Audio === "undefined") return;
  if (ringbackEl && !ringbackEl.paused) return; // already ringing
  const el = (ringbackEl ??= new Audio());
  el.loop = true;
  el.src = ringbackWav();
  void applySpeaker(el).finally(() => {
    if (el === ringbackEl && el.src) void el.play().catch(() => {});
  });
}
export function stopRingback() {
  const el = ringbackEl;
  if (!el || !el.src) return;
  el.pause();
  el.removeAttribute("src");
  el.load(); // lets go of it: nothing left "playing" (the Mac / iPhone Now Playing card)
  clearNowPlaying();
}

/** A burst of filtered noise with its own envelope points (for cuts, snips, tears). */
function noiseShape(o: Out, t: number, x: { type: BiquadFilterType; f: number; f2?: number; q?: number; env: [number, number][]; pan?: number; rand?: (i: number) => number }) {
  const { ctx } = o;
  const d = x.env[x.env.length - 1][0];
  const len = Math.ceil((d + 0.05) * ctx.sampleRate);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let seed = 0x1f2e3d4c;
  for (let i = 0; i < len; i++) {
    seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5;
    data[i] = (((seed >>> 0) / 0xffffffff) * 2 - 1) * (x.rand ? x.rand(i / ctx.sampleRate) : 1);
  }
  const at = Math.max(t, ctx.currentTime);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = x.type;
  f.Q.value = x.q ?? 1;
  f.frequency.setValueAtTime(x.f, at);
  if (x.f2) f.frequency.exponentialRampToValueAtTime(x.f2, at + d);
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, at);
  for (const [dt, g] of x.env) env.gain.exponentialRampToValueAtTime(Math.max(0.0001, g), at + dt);
  let tail: AudioNode = env;
  src.connect(f).connect(env);
  if (x.pan && typeof ctx.createStereoPanner === "function") {
    const pn = ctx.createStereoPanner();
    pn.pan.value = x.pan;
    env.connect(pn);
    tail = pn;
  }
  tail.connect(o.dest);
  src.start(at);
  src.stop(at + d + 0.03);
  o.nodes.add(src);
  src.onended = () => {
    o.nodes.delete(src);
    f.disconnect();
    env.disconnect();
    if (tail !== env) tail.disconnect();
  };
}
/** Two steel blades meeting: a bright click with a short metallic ring. */
function blade(o: Out, t: number, g: number, pitch = 1, pan = 0) {
  noiseShape(o, t, { type: "bandpass", f: 5200 * pitch, q: 1.4, env: [[0.002, g], [0.035, g * 0.05], [0.05, 0.0001]], pan });
  noiseShape(o, t, { type: "highpass", f: 9000, env: [[0.001, g * 0.6], [0.012, 0.0001]], pan });
  for (const [f, k] of [[3150, 1], [4870, 0.6], [7210, 0.35]] as const) tone(o, { f: f * pitch, t, d: 0.09, g: g * 0.07 * k, a: 0.001 });
}

/**
 * The other side hung up (24.10.18). "Call ended" sounds: a cut, like scissors snipping the line. Played once the
 * call's sound is off (the engine is quiet during a call).
 */
export const HANGUP_SOUNDS: { id: string; label: string; play: (o: Out, t: number) => void }[] = [
  // One clean scissors snip: the blades slide (a soft swish), then meet.
  { id: "snip", label: "Snip", play: (o, t) => {
    noiseShape(o, t, { type: "bandpass", f: 2600, f2: 6400, q: 0.9, env: [[0.03, 0.05], [0.08, 0.09], [0.1, 0.0001]] });
    blade(o, t + 0.095, 0.5);
  } },
];
/** Short clicks sound quieter than a ring at the same peak: lifted to about the text sound's loudness. */
const HANGUP_LEVEL = 1.8;
export function playHangupTone(id?: string) {
  const prefs = getSoundPrefs();
  if (!prefs.hangupTone) return;
  const s = HANGUP_SOUNDS.find((x) => x.id === (id ?? "snip")) ?? HANGUP_SOUNDS[0];
  const o = engine.group("hangup");
  if (!o) return;
  o.send.gain.value = 0.08; // a touch of room
  (o.dest as GainNode).gain.value = HANGUP_LEVEL;
  s.play(o, o.ctx.currentTime + 0.03);
}
/** Renders a call-ended sound offline (to hear the choices side by side). */
export async function renderHangupSound(id: string, secs = 1, sampleRate = 44100): Promise<AudioBuffer | null> {
  const s = HANGUP_SOUNDS.find((x) => x.id === id);
  if (!s) return null;
  const ctx = new OfflineAudioContext(2, Math.ceil(secs * sampleRate), sampleRate);
  const { master, reverbIn } = buildChain(ctx, ctx.destination);
  master.gain.value = 0.7 * 0.7 * 1.2; // the default 70% volume
  const gain = ctx.createGain();
  gain.gain.value = HANGUP_LEVEL;
  gain.connect(master);
  const send = ctx.createGain();
  send.gain.value = 0.08;
  gain.connect(send).connect(reverbIn);
  s.play({ ctx, dest: gain, send, nodes: new Set() }, 0.05);
  return ctx.startRendering();
}

const ringers = new Set<Ringer>();

/** Stops every sound (ringtones, previews, alerts) right away. */
export function stopAll() {
  for (const r of ringers) r.stop();
  engine.stopAll();
}

/** Unlocks audio (call from any tap). Same as `new Ringer().unlock()`. */
export function unlockAudio() {
  engine.unlock();
}

/** Loops the chosen ringtone (with vibration on phones) until stop(). */
export class Ringer {
  private timer: number | undefined;
  private readonly name = `ring-${Math.random().toString(36).slice(2)}`;

  /** Browsers only allow sound after a tap; call this from any tap to arm the ringer. */
  unlock() {
    engine.unlock();
  }

  get ringing() {
    return this.timer !== undefined;
  }

  start() {
    if (this.timer !== undefined) return;
    ringers.add(this);
    const id = getSoundPrefs().ringtone;
    const pattern = RING[id];
    const period = pattern?.period ?? 2.5;
    const ring = () => {
      if (pattern && engine.ready) {
        const o = engine.group(this.name, false, !!pattern.sample);
        if (o) {
          o.send.gain.value = pattern.reverb ?? 0.18;
          (o.dest as GainNode).gain.value = pattern.level ?? 1;
          pattern.play(o, o.ctx.currentTime + 0.03);
        }
      }
      navigator.vibrate?.([400, 200, 400]);
    };
    ring();
    this.timer = window.setInterval(ring, period * 1000);
  }

  stop() {
    if (this.timer === undefined) return;
    clearInterval(this.timer);
    this.timer = undefined;
    ringers.delete(this);
    engine.stop(this.name);
    navigator.vibrate?.(0);
  }
}

/** Renders a ringtone offline (for the sound previewer and level checks). Loops it to fill `secs`. */
export async function renderRingtone(id: string, secs = 6, sampleRate = 44100): Promise<AudioBuffer | null> {
  const p = RING[id];
  if (!p) return null;
  const ctx = new OfflineAudioContext(2, Math.ceil(secs * sampleRate), sampleRate);
  const { master, direct, reverbIn } = buildChain(ctx, ctx.destination);
  master.gain.value = direct.gain.value = 0.7 * 0.7 * 1.2; // the default 70% volume
  const gain = ctx.createGain();
  gain.gain.value = p.level ?? 1;
  gain.connect(p.sample ? direct : master);
  const send = ctx.createGain();
  send.gain.value = p.reverb ?? 0.18;
  gain.connect(send).connect(reverbIn);
  const o: Out = { ctx, dest: gain, send, nodes: new Set() };
  if (p.sample) await loadSample(ctx, p.sample);
  for (let at = 0.03; at < secs - 0.5; at += p.period) p.play(o, at);
  return ctx.startRendering();
}
